import { useState, useEffect } from "react";
import { Row, Col, Tabs, Dropdown, Menu } from "antd";
import {
  EditOutlined,
  FilePdfOutlined,
  SelectOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import DataTable from "../../DataTable";
import CopyableCell from "../../CopyableCell";

const { TabPane } = Tabs;

interface IInvoiceProps {
  invoices: any;
  showEditModal: Function;
  showQuoteModal: Function;
  showInvoiceModal: Function;
  handleManage?: Function;
  handleDelete: Function;
  filtered: any;
}

const FuneralInvoiceTable: React.FC<IInvoiceProps> = ({
  invoices,
  showEditModal,
  handleManage,
  showQuoteModal,
  showInvoiceModal,
  handleDelete,
  filtered,
}) => {
  const [allQuotes, setAllQuotes] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);

  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);

  const [state, setState] = useState({
    loading: true,
    activeKey: sessionStorage.getItem("funeralInvoicesActiveTab") || "1",
    pagination: {
      pageSize: 15,
      position: ["bottomRight"],
      total: 0,
      current: Number(sessionStorage.getItem("funeralInvoicesPage")) || 1,
      hideOnSinglePage: true,
    },
    searchItem: "",
  });

  const fetchQuotes = async (params = {}) => {
    try {
      const quotes = invoices.filter((item: any) => item.Type === "Quote");

      if (filtered.length > 0) {
        const quotesFiltered = filtered.filter(
          (item: any) => item.Type === "Quote"
        );
        setFilteredQuotes(quotesFiltered);
      }

      const pagination = state.pagination;
      pagination.total = filtered.length.toString();

      setState({
        ...state,
        loading: false,
        pagination,
        ...params,
      });

      setAllQuotes(quotes);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchInvoices = async (params = {}) => {
    try {
      const allInvoices = invoices.filter(
        (item: any) => item.Type === "Invoice"
      );

      if (filtered.length > 0) {
        const invoicesFiltered = filtered.filter(
          (item: any) => item.Type === "Invoice"
        );
        setFilteredInvoices(invoicesFiltered);
      }

      const pagination = state.pagination;
      pagination.total = filtered.length.toString();

      setState({
        ...state,
        loading: false,
        pagination,
        ...params,
      });

      setAllInvoices(allInvoices);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (filtered.length === 0) {
      setFilteredQuotes([]);
      setFilteredInvoices([]);
    }
    fetchQuotes();
    fetchInvoices();
  }, [invoices, filtered]);

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    const pager = { ...state.pagination };
    pager.current = pagination.current;
    sessionStorage.setItem("funeralInvoicesPage", String(pagination.current));
    setState({
      ...state,
      pagination: pager,
    });

    const params = {
      pageSize: pagination.pageSize,
      page: pagination.current,
      sortField: sorter.field,
      sortOrder: sorter.order,
      ...filters,
    };

    fetchQuotes(params);
  };

  const columns = [
    {
      title: state.activeKey === "1" ? "Invoice No" : "Quote No",
      dataIndex: state.activeKey === "1" ? "InvoiceNumber" : "QuoteNumber",
    },

    {
      title: "Type",
      dataIndex: "Type",
    },
    {
      title: "Customer ID",
      dataIndex: ["FuneralCustomer", "IdNumber"],
      render: (v: any) => <CopyableCell value={v} />,
    },
    {
      title: "Date",
      dataIndex: "date",
      render: (date: Date) => dayjs(date).format("YYYY-MM-DD HH:mm:ss"),
      sorter: (a: any, b: any) => dayjs(a.Date).unix() - dayjs(b.Date).unix(),
    },
    {
      dataIndex: "Action",
      render: (_: any, record: any) => (
        <Dropdown
          overlay={
            <Menu>
              {handleManage && (
                <Menu.Item
                  key="manage"
                  icon={<SelectOutlined style={{ color: "green" }} />}
                  onClick={() => handleManage(record.id)}
                >
                  Manage Invoice
                </Menu.Item>
              )}
              <Menu.Item
                key="edit"
                icon={<EditOutlined style={{ color: "blue" }} />}
                onClick={() => showEditModal(record)}
              >
                Edit Invoice
              </Menu.Item>
              {state.activeKey === "1" ? (
                <Menu.Item
                  key="download"
                  icon={<FilePdfOutlined style={{ color: "purple" }} />}
                  onClick={() => showInvoiceModal(record)}
                >
                  Download Invoice
                </Menu.Item>
              ) : (
                <Menu.Item
                  key="download"
                  icon={<FilePdfOutlined style={{ color: "purple" }} />}
                  onClick={() => showQuoteModal(record)}
                >
                  Download Quote
                </Menu.Item>
              )}
            </Menu>
          }
          trigger={["click"]}
        >
          <MoreOutlined
            rotate={90}
            style={{ cursor: "pointer", fontSize: "16px", color: "#000" }}
          />
        </Dropdown>
      ),
    },
  ];

  if (state.activeKey === "1") {
    columns.splice(1, 0, {
      title: "Quote No",
      dataIndex: "QuoteNumber",
    });
  }

  const tabPanes = [
    {
      key: "1",
      title: "Invoice",
      content: (
        <DataTable
          persistKey="funeral-invoices"
          dataSource={
            filteredInvoices.length > 0 ? filteredInvoices : allInvoices
          }
          columns={columns}
          loading={state.loading}
          pagination={state.pagination}
          onChange={handleTableChange}
        />
      ),
      count:
        filteredInvoices.length > 0
          ? filteredInvoices.length
          : allInvoices.length,
    },
    {
      key: "2",
      title: "Quote",
      content: (
        <DataTable
          persistKey="funeral-invoices"
          dataSource={filteredQuotes.length > 0 ? filteredQuotes : allQuotes}
          columns={columns}
          loading={state.loading}
          pagination={state.pagination}
          onChange={handleTableChange}
        />
      ),
      count: fetchQuotes.length > 0 ? fetchQuotes.length : allQuotes.length,
    },
  ];

  const changeTab = (key: any) => {
    const pagination = { ...state.pagination, current: 1 };
    sessionStorage.setItem("funeralInvoicesActiveTab", key);
    sessionStorage.setItem("funeralInvoicesPage", "1");
    setState({
      ...state,
      activeKey: key,
      loading: false,
      pagination,
    });
  };

  return (
    <>
      <Row>
        <Col span={24}>
          <Tabs
            type="card"
            defaultActiveKey={state.activeKey}
            activeKey={state.activeKey}
            onChange={changeTab}
          >
            {tabPanes.map((pane) => (
              <TabPane
                key={pane.key}
                tab={
                  <>
                    <span>{pane.title}</span>
                    <span
                      style={{
                        color: "#B0B0B0",
                        fontWeight: "300",
                        marginLeft: "3px",
                        fontSize: "12px",
                      }}
                    >
                      ({pane.count})
                    </span>
                  </>
                }
              >
                {pane.content}
              </TabPane>
            ))}
          </Tabs>
        </Col>
      </Row>
    </>
  );
};

export default FuneralInvoiceTable;
